import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import {
  Gamepad2, Mic, Brain, Trophy, KeyRound, Sparkles, ChevronRight, Star,
} from "lucide-react";

const LAUNCH_GAMES = [
  {
    to: "/play/hosted",
    icon: Mic,
    badge: "MULTIPLAYER",
    title: { fr: "Quiz hôte avec code", en: "Host a quiz with a code", es: "Anfitrión de quiz con código", pt: "Anfitrião de quiz com código" },
    desc: {
      fr: "geoVINUM AI génère un quiz personnalisé, vous obtenez un code à 6 chiffres à partager à vos amis.",
      en: "geoVINUM AI builds a personalised quiz, you get a 6-digit code to share with friends.",
      es: "geoVINUM AI crea un quiz personalizado y obtienes un código de 6 dígitos para compartir con amigos.",
      pt: "geoVINUM AI cria um quiz personalizado e obtém um código de 6 dígitos para partilhar com os amigos.",
    },
    cost: { fr: "1 crédit pour générer", en: "1 credit to generate", es: "1 crédito para generar", pt: "1 crédito para gerar" },
    testId: "play-launch-hosted",
  },
  {
    to: "/play/maestro",
    icon: Mic,
    badge: "MAESTRO",
    title: { fr: "Maestro — Quiz avec équipes et QR code", en: "Maestro — Team quiz with QR code", es: "Maestro — Quiz por equipos con QR", pt: "Maestro — Quiz por equipas com QR" },
    desc: {
      fr: "Créez votre quiz sur mesure : équipes, timer, QR code à afficher. Idéal pour dégustations, salons ou cours.",
      en: "Build your own quiz: teams, timer, on-screen QR code. Perfect for tastings, fairs and classes.",
      es: "Crea tu propio quiz: equipos, temporizador, QR en pantalla. Ideal para catas, ferias y clases.",
      pt: "Crie o seu quiz: equipas, timer, QR no ecrã. Ideal para provas, feiras e aulas.",
    },
    cost: { fr: "Gratuit", en: "Free", es: "Gratis", pt: "Gratuito" },
    testId: "play-launch-maestro",
  },
  {
    to: "/play/solo",
    icon: Brain,
    badge: "SOLO",
    title: { fr: "Quiz éclair, Memory & Ranking", en: "Quick quiz, Memory & Ranking", es: "Quiz rápido, Memoria y Ranking", pt: "Quiz rápido, Memória e Ranking" },
    desc: {
      fr: "3 mini-jeux solo en 60 secondes : quiz éclair, paires de cépages, classement par latitude.",
      en: "3 solo mini-games in 60s: quick quiz, grape pairs, latitude ranking.",
      es: "3 mini-juegos en solitario en 60s: quiz rápido, pares de uvas, ranking por latitud.",
      pt: "3 mini-jogos a solo em 60s: quiz rápido, pares de castas, ranking por latitude.",
    },
    cost: { fr: "Gratuit", en: "Free", es: "Gratis", pt: "Grátis" },
    testId: "play-launch-solo",
  },
  {
    to: "/audrey",
    icon: Sparkles,
    badge: "SOLO",
    title: { fr: "geoVINUM AI Guide", en: "geoVINUM AI Guide", es: "geoVINUM AI Guía", pt: "geoVINUM AI Guia" },
    desc: {
      fr: "geoVINUM AI vous fait deviner où vous êtes : un terroir mystère à débusquer à coup d'indices et de questions.",
      en: "geoVINUM AI makes you guess a hidden terroir with clues and Q&A.",
      es: "geoVINUM AI te hace adivinar un terruño misterioso con pistas y preguntas.",
      pt: "geoVINUM AI faz-lhe adivinhar um terroir misterioso com pistas e perguntas.",
    },
    cost: { fr: "Gratuit", en: "Free", es: "Gratis", pt: "Grátis" },
    testId: "play-launch-audrey",
  },
  {
    to: "/multiplayer",
    icon: Gamepad2,
    badge: "MULTIPLAYER",
    title: { fr: "Lobby Multiplayer classique", en: "Classic Multiplayer lobby", es: "Lobby Multijugador clásico", pt: "Lobby Multijogador clássico" },
    desc: {
      fr: "Salles publiques de quiz, rejoignez la prochaine partie en cours.",
      en: "Public quiz rooms, join the next game in progress.",
      es: "Salas públicas de quiz, únete a la próxima partida en curso.",
      pt: "Salas públicas de quiz, entre na próxima partida em curso.",
    },
    cost: { fr: "Gratuit", en: "Free", es: "Gratis", pt: "Grátis" },
    testId: "play-launch-multiplayer",
  },
];

const HEAD = {
  chip: { fr: "Lancer un jeu", en: "Launch a game", es: "Lanzar un juego", pt: "Lançar um jogo" },
  title: { fr: "Quel jeu voulez-vous lancer ?", en: "What do you want to play?", es: "¿Qué juego quieres lanzar?", pt: "Que jogo quer lançar?" },
  join: { fr: "Rejoindre une partie", en: "Join a game", es: "Unirse a una partida", pt: "Entrar numa partida" },
  rewards: { fr: "Mes récompenses", en: "My rewards", es: "Mis recompensas", pt: "As minhas recompensas" },
  intro: {
    fr: "Choisissez votre mode : solo en 60 s, défi multijoueur avec un code, ou laissez geoVINUM AI vous guider sur un terroir mystère.",
    en: "Pick your mode: 60s solo, multiplayer challenge with a code, or let geoVINUM AI guide you through a mystery terroir.",
    es: "Elige tu modo: solo en 60s, desafío multijugador con un código, o deja que geoVINUM AI te guíe por un terruño misterioso.",
    pt: "Escolha o seu modo: solo em 60s, desafio multijogador com um código, ou deixe a geoVINUM AI guiá-lo num terroir misterioso.",
  },
  streakTitle: { fr: "Jouez quotidiennement pour bâtir votre série", en: "Play daily to build your streak", es: "Juega diariamente para construir tu racha", pt: "Jogue diariamente para construir a sua sequência" },
  streakDesc: { fr: "Chaque partie gagnée vous fait monter d'un niveau et débloque des badges.", en: "Each win levels you up and unlocks badges.", es: "Cada victoria te sube de nivel y desbloquea insignias.", pt: "Cada vitória sobe-o de nível e desbloqueia distintivos." },
  seeBadges: { fr: "Voir mes badges →", en: "See my badges →", es: "Ver mis insignias →", pt: "Ver os meus distintivos →" },
};

export default function PlayHome() {
  const { user } = useAuth();
  const { lang } = useT();
  const pick = (o) => o[lang] || o.fr;

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="play-home">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="chip-pink inline-flex items-center gap-1.5"><Gamepad2 className="w-3 h-3" /> {pick(HEAD.chip)}</span>
          <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
            {pick(HEAD.title)}
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/play/join" data-testid="play-home-join-link"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#1F3A5F]/15 text-xs font-bold text-[#1F3A5F] hover:border-[#E5276A] hover:text-[#E5276A]">
            <KeyRound className="w-3.5 h-3.5" /> {pick(HEAD.join)}
          </Link>
          <Link to="/play/rewards" data-testid="play-home-rewards-link"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#1F3A5F]/15 text-xs font-bold text-[#1F3A5F] hover:border-[#E5276A] hover:text-[#E5276A]">
            <Trophy className="w-3.5 h-3.5" /> {pick(HEAD.rewards)}
          </Link>
        </div>
      </div>
      <p className="text-[#4A5568] mt-3 max-w-2xl">
        {pick(HEAD.intro)}
      </p>

      <div className="mt-10 grid md:grid-cols-2 gap-5">
        {LAUNCH_GAMES.map((g) => (
          <Link
            key={g.to}
            to={g.to}
            data-testid={g.testId}
            className="app-card p-6 hover:border-[#E5276A] transition-all group block relative"
          >
            <span className={`absolute top-4 right-4 text-[9px] tracking-widest font-bold px-2 py-0.5 rounded-full ${
              g.badge === "SOLO" ? "bg-[#1F3A5F]/10 text-[#1F3A5F]" : "bg-[#FCE4ED] text-[#E5276A]"
            }`}>
              {g.badge}
            </span>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#FBF7F3] flex items-center justify-center shrink-0 group-hover:bg-[#E5276A] transition-colors">
                <g.icon className="w-6 h-6 text-[#1F3A5F] group-hover:text-white" />
              </div>
              <div className="min-w-0 flex-1 pr-16">
                <h2 className="font-display text-xl text-[#1F3A5F] leading-tight">{pick(g.title)}</h2>
                <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">{pick(g.desc)}</p>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <span className="text-[11px] uppercase tracking-widest text-[#E5276A] font-bold">{pick(g.cost)}</span>
                  <ChevronRight className="w-4 h-4 text-[#8B95A1] group-hover:text-[#E5276A]" />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {user && (
        <div className="mt-10 app-card p-5 flex items-center gap-4 border-l-4 border-[#E5276A]" data-testid="play-home-streak-banner">
          <Star className="w-8 h-8 text-amber-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#1F3A5F]">{pick(HEAD.streakTitle)}</p>
            <p className="text-xs text-[#4A5568] mt-1">{pick(HEAD.streakDesc)}</p>
          </div>
          <Link to="/play/rewards" className="ml-auto text-xs font-bold text-[#E5276A] hover:underline shrink-0">
            {pick(HEAD.seeBadges)}
          </Link>
        </div>
      )}
    </div>
  );
}
